import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { ChangeableNoteData, CreateNotePayload, EditNotePayload } from '@/domains/notes/store/types/createNoteTypes';
import { GetDocumentsFromEntitiesPayload } from '@/domains/notes/store/types/noteTypes';
import { SelectOption } from '@/features/editMode/types/selectOption';
import { FileWithPath } from 'react-dropzone';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { selectCurrentClaimSetNumber } from '@/domains/claimSets/store/selectors/claimSetSliceSelectors';
import { DocumentEntityTypeId } from '@/domains/documents/enums/DocumentEntityTypeId';
import NoteEvents from '@/domains/notes/events';
import { refreshNoteSaga } from '@/domains/notes/sagas/notesWatcher';
import { resetCreateDocumentState } from '@/domains/notes/store/slices/createNoteDocumentSlice';
import {
  createNote,
  editNote,
  getClaimSetDocuments,
  getDocumentsFromEntities,
  getDocumentsFromEntitiesSuccess,
  getEntityDocumentOptionEntitiesSuccess,
  resetCreateNoteState,
  setDocumentOptionsLoading,
  setOptionsAreLoading,
} from '@/domains/notes/store/slices/createNoteSlice';
import { getNoteList, setNoteUpdateInProgress } from '@/domains/notes/store/slices/noteSlice';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import TypeService from '@/services/TypeService/TypeService';

function dispatchUpdatedEntityEvent({
  claimSetId,
  entityKey,
  entityType,
}: {
  claimSetId: string | number;
  entityType: EdsEntityCodes;
  entityKey: string | number | null;
}) {
  try {
    if (entityType === EdsEntityCodes.ClaimSet && entityKey !== null)
      return EventService.dispatch(
        new NoteEvents.NoteCreated({
          entityType: EdsEntityCodes.ClaimSet,
          entityKey: String(claimSetId),
        }),
      );
    else if (entityType === EdsEntityCodes.ClaimEntity && entityKey !== null)
      return EventService.dispatch(
        new NoteEvents.NoteCreated({ entityType: EdsEntityCodes.ClaimEntity, entityKey: String(entityKey) }),
      );
    else if (entityType === EdsEntityCodes.Claimant && entityKey !== null)
      return EventService.dispatch(
        new NoteEvents.NoteCreated({ entityType: EdsEntityCodes.Claimant, entityKey: String(entityKey) }),
      );
  } catch (error) {
    console.error(error);
  }
}

const detectChangableNoteData = (newNoteData: ChangeableNoteData, previousNoteData: ChangeableNoteData) =>
  Object.fromEntries(
    Object.entries(newNoteData).map(([key, value]) => {
      if (TypeService.isEqual(value, previousNoteData?.[key])) {
        return [key, null];
      }
      if (key === 'documents') {
        const newExistingDocuments = (value as ChangeableNoteData['documents'])
          ?.filter((document) => !!document['existingDocumentId'])
          .map((document) => document['existingDocumentId']);
        const previousExistingDocuments = previousNoteData?.[key]
          ?.filter((document) => !!document['existingDocumentId'])
          .map((document) => document['existingDocumentId']);
        if (TypeService.isEqual(newExistingDocuments, previousExistingDocuments)) {
          // send only new document and skip existing since they are equal
          return [key, (value as ChangeableNoteData['documents'])?.filter((document) => !!document['newDocument'])];
        }
      }
      return [key, value];
    }),
  ) as ChangeableNoteData;

const prepareFormDataForNoteUpsert = (
  changeableNoteData: ChangeableNoteData,
  claimSetId?: string,
  extraFormValues?: Array<[string, string | null | undefined]>,
) => {
  const formKeyValues: Array<[string, string | FileWithPath | null | undefined]> = [
    ...((claimSetId ? [['claimSetId', claimSetId]] : [[]]) as [string, string][]),
    ['entityId', changeableNoteData.entityId?.toString()],
    ['noteTypeId', changeableNoteData.noteTypeId?.toString()],
    ['title', changeableNoteData.title],
    ['description', changeableNoteData.description],
    ['pinned', changeableNoteData.pinned?.toString()],
    ...(extraFormValues || []),
  ];
  const reminderDateTime = changeableNoteData.reminder?.reminderDateTime?.toString();
  const accountToNotifyId = changeableNoteData.reminder?.accountToNotifyId?.toString();

  if (reminderDateTime && accountToNotifyId) {
    formKeyValues.push(['reminderDateTime', reminderDateTime], ['accountToNotifyId', accountToNotifyId]);
  }

  for (const document of changeableNoteData.documents || []) {
    if ('existingDocumentId' in document) {
      formKeyValues.push(['existingDocumentsIds', document.existingDocumentId.toString()]);
    } else {
      formKeyValues.push(
        ['newDocument', document.newDocument?.file || ''],
        ['documentTypeId', document.newDocument?.documentTypeId.toString() || ''],
        ['entityType', edsToEntityFormat(document.newDocument?.entityType || '')],
        ['entityKey', document.newDocument?.entityKey.toString() || ''],
        ...(document.newDocument?.createNewInstance
          ? [['createNewInstance', document.newDocument.createNewInstance] as [string, string]]
          : ([] as [string, string][])),
        ...(document.newDocument?.preferredExistingDocumentEntityId != null
          ? [
              [
                'preferredExistingDocumentEntityId',
                document.newDocument.preferredExistingDocumentEntityId?.toString(),
              ] as [string, string],
            ]
          : ([] as [string, string][])),
      );
    }
  }
  const formData = new FormData();

  for (const [formKey, formValue] of formKeyValues) {
    if (formValue === undefined || formValue === null) {
      continue;
    }
    formData.append(formKey, formValue);
  }
  return formData;
};

export function* createNoteSaga({ payload: { newNoteData } }: PayloadAction<CreateNotePayload>) {
  yield put(setSystemLoadingStatus(true));

  try {
    const claimSetId = yield select(selectCurrentClaimSetId);
    const formData = prepareFormDataForNoteUpsert(newNoteData, claimSetId);

    yield call(HttpService.post, `/${EdsEntityCodes.Note}`, formData, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: `Note has been added successfully`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    if (newNoteData.entityType)
      dispatchUpdatedEntityEvent({
        entityType: newNoteData.entityType as EdsEntityCodes,
        entityKey: newNoteData.entityId,
        claimSetId,
      });

    yield put(
      getNoteList({
        filters: [
          { propertyName: EdsFilterEntities.ClaimSetId, values: [claimSetId] },
          {
            propertyName: EdsFilterEntities.RelatedEntityKey,
            values: [`${newNoteData.entityType}-${newNoteData.entityId}`],
          },
        ],
      }),
    );
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetCreateNoteState());
    yield put(resetCreateDocumentState());
  }
}

export function* editNoteSaga({
  payload: { newNoteData, previousNoteData, editableNoteId },
}: PayloadAction<EditNotePayload>) {
  try {
    yield put(setNoteUpdateInProgress({ noteId: editableNoteId, value: true }));

    const changeableNoteData = detectChangableNoteData(newNoteData, previousNoteData);
    const claimSetId = yield select(selectCurrentClaimSetId);

    let extraReminderFields: [string, string | null][] = [];

    if (!newNoteData.reminder && previousNoteData.reminder) {
      extraReminderFields = [['setReminder', 'false'] as const];
    } else if (!previousNoteData.reminder && newNoteData.reminder) {
      extraReminderFields = [['setReminder', 'true'] as const];
    } else if (!TypeService.isEqual(newNoteData.reminder, previousNoteData.reminder)) {
      extraReminderFields = [['setReminder', null] as const];
    }
    const formData = prepareFormDataForNoteUpsert(changeableNoteData, undefined, extraReminderFields);

    const requestUrl = `/${EdsEntityCodes.Note}/${editableNoteId}`;

    yield call(HttpService.patch, requestUrl, formData, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: `Note has been edited successfully`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    if (formData.has('pinned')) {
      yield put(getNoteList({ filters: [{ propertyName: 'ClaimSetId', values: [claimSetId] }] }));
    } else {
      yield call(refreshNoteSaga, { payload: editableNoteId, type: '' });
    }
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setNoteUpdateInProgress({ noteId: editableNoteId, value: false }));

    yield put(resetCreateNoteState());
    yield put(resetCreateDocumentState());
  }
}

export function* getDocumentsFromEntitiesSaga({ payload }: PayloadAction<GetDocumentsFromEntitiesPayload>) {
  try {
    yield put(setDocumentOptionsLoading(true));

    const requestUrl = `/filter/${EdsEntityCodes.Document}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 10000, includes: `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.DocumentVersions}` })}`;

    const { data } = yield call(HttpService.post, requestUrl, payload?.filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    yield put(getDocumentsFromEntitiesSuccess({ documents: normalizedResponse, entityType: payload.entityType }));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Documents fetching error',
      content: 'Unable to fetch documents. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setDocumentOptionsLoading(false));
  }
}

function* getNoteDocumentsClaimValuesSaga(claimSetId: number) {
  // TODO: align with code in document generation
  const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 10000 })}`;

  const { data } = yield call(
    HttpService.post,
    requestUrl,
    [{ propertyName: EdsFilterEntities.ClaimSetId, values: [claimSetId] }],
    {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    },
  );
  const claimIds: number[] = data.listings.map((claim) => claim.id);

  const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

  const claimOptions = normalizedResponse.reduce((acc, claimEntity) => {
    if (claimEntity?.virtualFields?.isDeleted) return acc;

    return [
      ...acc,
      { label: claimEntity.fields.claimNumber?.value?.toString() || '', value: claimEntity.id.toString() },
    ];
  }, [] as SelectOption[]);

  yield put(getEntityDocumentOptionEntitiesSuccess({ entities: claimOptions, entityType: DocumentEntityTypeId.Claim }));

  return claimIds;
}

function* getNoteDocumentsClaimantValuesSaga(claimIds: number[]) {
  if (claimIds.length === 0) {
    return [];
  }
  const requestUrl = `/filter/${EdsEntityCodes.Claimant}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 10000, includes: EdsEntityCodes.Claim })}`;

  const { data } = yield call(
    HttpService.post,
    requestUrl,
    [{ propertyName: EdsFilterEntities.ClaimId, values: claimIds }],
    {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    },
  );

  const claimantIds: number[] = data.listings.map((claim) => claim.id);
  const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);
  const claimantOptions = normalizedResponse.map((claimant) => ({
    label: `${claimant.fields.claimantName?.value} (${(claimant.entities.Claim as NormalizedV2Response)?.fields?.claimNumber?.value || 'unknown'})`,
    value: claimant.id.toString(),
  }));

  yield put(
    getEntityDocumentOptionEntitiesSuccess({
      entities: claimantOptions,
      entityType: DocumentEntityTypeId.Claimant,
    }),
  );

  return claimantIds;
}

export function* getClaimSetDocumentOptionsSaga() {
  try {
    yield put(setOptionsAreLoading(true));

    const claimSetId = yield select(selectCurrentClaimSetId);
    const claimSetNumber = yield select(selectCurrentClaimSetNumber);

    yield put(
      getEntityDocumentOptionEntitiesSuccess({
        entities: [{ label: claimSetNumber, value: claimSetId }],
        entityType: DocumentEntityTypeId.ClaimSet,
      }),
    );

    const getDocumentsFromClaimSet = call(getDocumentsFromEntitiesSaga, {
      type: '',
      payload: {
        entityType: DocumentEntityTypeId.ClaimSet,
        filters: [
          { propertyName: EdsFilterEntities.RelatedEntityKey, values: [`${EdsEntityCodes.ClaimSet}-${claimSetId}`] },
        ],
      },
    });

    const claimsIds: number[] = yield call(getNoteDocumentsClaimValuesSaga, claimSetId);

    if (claimsIds.length === 0) {
      yield getDocumentsFromClaimSet;
      return;
    }

    const getDocumentsFromClaims = call(getDocumentsFromEntitiesSaga, {
      type: '',
      payload: {
        entityType: DocumentEntityTypeId.Claim,
        filters: [
          {
            propertyName: EdsFilterEntities.RelatedEntityKey,
            values: claimsIds.map((id) => `${EdsEntityCodes.ClaimEntity}-${id}`),
          },
        ],
      },
    });
    const claimantsIds: number[] = yield call(getNoteDocumentsClaimantValuesSaga, claimsIds);

    if (claimantsIds.length === 0) {
      yield all([getDocumentsFromClaimSet, getDocumentsFromClaims]);
      return;
    }

    const getDocumentsFromClaimants = call(getDocumentsFromEntitiesSaga, {
      type: '',
      payload: {
        entityType: DocumentEntityTypeId.Claimant,
        filters: [
          {
            propertyName: EdsFilterEntities.RelatedEntityKey,
            values: claimantsIds.map((id) => `${EdsEntityCodes.Claimant}-${id}`),
          },
        ],
      },
    });

    yield all([getDocumentsFromClaimSet, getDocumentsFromClaims, getDocumentsFromClaimants]);
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setOptionsAreLoading(false));
  }
}

export function* createNoteWatcher() {
  yield takeLatest([createNote.type], createNoteSaga);
  yield takeLatest([editNote.type], editNoteSaga);
  yield takeEvery([getDocumentsFromEntities.type], getDocumentsFromEntitiesSaga);
  yield takeLatest([getClaimSetDocuments.type], getClaimSetDocumentOptionsSaga);
}
